import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  confirmForgotPasswordApi,
  createAdminByAdminApi,
  forgotPasswordApi,
  loginApi,
  refreshTokenApi,
  resetAdminPasswordApi,
  resendConfirmationCodeApi,
  signupApi,
  verifyEmailApi,
  changePasswordApi,
  ssoLoginApi,
} from "../../services/authService";
import { ERROR_MESSAGES, LOGIN_API_RESULT_KIND, SUCCESS_MESSAGES, AUTH_ERROR_REASON } from "../../utils/constants";
import { tokenStorage, TOKEN_KEYS } from "../../utils/tokenStorage";
import {
  type AuthToken,
  type AuthErrorResponse,
  type ForgotPasswordResponse,
  type ConfirmForgotPasswordResponse,
  type LoginCredentials,
  type LoginFulfilledPayload,
  type SignupCredentials,
  type ResetPasswordCredentials,
  type ChangePasswordCredentials,
  type SSOLoginCredentials,
  type CreateAdminRequest,
  type AdminPasswordChallengeState,
} from "../../types/auth";
import { checkIsAdmin, checkIsSuperAdmin, getUserIdFromAccessToken } from "../../utils/helpers";
import {
  getForgotPasswordRejectedMessage,
  hasVerifyEmailAction,
  isUserNotConfirmedLoginResponse,
  isBlockedByAdminLoginResponse,
  isUserNotFoundLoginResponse,
  isEmailRegisteredViaSsoSignupResponse,
} from "../../utils/authHelpers";

const getApiErrorMessage = (error: any, fallback: string) => {
  const responseData = error?.response?.data;
  return (
    responseData?.message ||
    responseData?.data ||
    error?.message ||
    fallback
  );
};

interface AuthState {
  token: string | null;
  accessToken: string | null;
  userAccessToken: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
  expiresIn: string | null;
  userId?: string | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  success?: boolean;
  message?: string | null;
  adminPasswordChallenge: AdminPasswordChallengeState | null;
  adminInviteLoading: boolean;
}

const storedTokens = tokenStorage.getTokens();
const initialState: AuthState = {
  token: storedTokens.accessToken,
  accessToken: storedTokens.accessToken,
  userAccessToken: storedTokens.userAccessToken,
  refreshToken: storedTokens.refreshToken,
  expiresIn: storedTokens.expiresIn,
  userId: storedTokens.userId,
  isAdmin: checkIsAdmin(storedTokens.accessToken),
  isSuperAdmin: checkIsSuperAdmin(storedTokens.accessToken) || false,
  loading: false,
  error: null,
  success: false,
  message: null,
  adminPasswordChallenge: null,
  adminInviteLoading: false,
};

// Helper function to update tokens in both Redux state and localStorage
const updateTokens = (state: AuthState, tokens: AuthToken): void => {
  state.token = tokens.id_token;
  state.accessToken = tokens.access_token ?? null;
  state.userAccessToken = tokens.access_token ?? null;
  state.userId = tokens.userId;
  state.refreshToken = tokens.refresh_token;
  state.isAdmin = checkIsAdmin(tokens.id_token);
  state.isSuperAdmin = checkIsSuperAdmin(tokens.id_token);
  state.loading = false;
  state.error = null;
  state.adminPasswordChallenge = null;
  tokenStorage.setTokens(tokens);
};

// Async thunk for login
export const login = createAsyncThunk<
  LoginFulfilledPayload,
  LoginCredentials,
  { rejectValue: AuthErrorResponse }
>("auth/login", async (credentials, thunkAPI) => {
  try {
    const data = await loginApi(credentials.email, credentials.password);
    if (data.kind === LOGIN_API_RESULT_KIND.NEW_PASSWORD_REQUIRED) {
      return {
        outcome: LOGIN_API_RESULT_KIND.NEW_PASSWORD_REQUIRED,
        email: credentials.email,
        session: data.session,
        challenge: data.challenge,
      };
    }
    return {
      outcome: LOGIN_API_RESULT_KIND.SESSION,
      tokens: {
        id_token: data.id_token,
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_in: data.expires_in,
        userId: data.userId,
      },
    };
  } catch (error: any) {
    const data = error.response?.data as Record<string, unknown> | undefined;
    if (isUserNotConfirmedLoginResponse(data)) {
      return thunkAPI.rejectWithValue({
        message:
          (typeof data?.message === "string" && data.message) ||
          ERROR_MESSAGES.LOGIN_FAILED,
        statusCode: 403,
        reason: AUTH_ERROR_REASON.USER_NOT_CONFIRMED,
        email: credentials.email,
      });
    }
    if (isBlockedByAdminLoginResponse(data)) {
      return thunkAPI.rejectWithValue({
        message:
          (typeof data?.message === "string" && data.message) ||
          ERROR_MESSAGES.ACCOUNT_SUSPENDED,
        statusCode: 403,
        reason: AUTH_ERROR_REASON.BLOCKED_BY_ADMIN,
        isBlockedByAdmin: true,
      });
    }
    if (isUserNotFoundLoginResponse(data)) {
      return thunkAPI.rejectWithValue({
        message: ERROR_MESSAGES.LOGIN_USER_ACCOUNT_NOT_EXIST,
        statusCode: 404,
      });
    }
    return thunkAPI.rejectWithValue({
      message:
        (typeof data?.message === "string" && data.message) ||
        error.message ||
        ERROR_MESSAGES.LOGIN_FAILED,
      statusCode:
        typeof data?.statusCode === "number" ? data.statusCode : undefined,
    });
  }
});

export const inviteAdmin = createAsyncThunk<
  { message: string },
  CreateAdminRequest,
  { rejectValue: AuthErrorResponse }
>("auth/inviteAdmin", async (payload, thunkAPI) => {
  const isAdmin = (thunkAPI.getState() as RootState).auth.isAdmin;
  if (!isAdmin) {
    return thunkAPI.rejectWithValue({
      message: ERROR_MESSAGES.ADMIN_INVITE_FORBIDDEN,
    });
  }
  try {
    await createAdminByAdminApi(payload);
    return { message: SUCCESS_MESSAGES.ADMIN_INVITE_SENT };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      message:
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.ADMIN_INVITE_FAILED,
    });
  }
});

export const resetAdminPassword = createAsyncThunk<
  AuthToken,
  { newPassword: string },
  { rejectValue: AuthErrorResponse; state: RootState }
>("auth/resetAdminPassword", async ({ newPassword }, thunkAPI) => {
  const challenge = (thunkAPI.getState() as RootState).auth
    .adminPasswordChallenge;
  if (!challenge?.email || !challenge?.session) {
    return thunkAPI.rejectWithValue({
      message: ERROR_MESSAGES.ADMIN_SET_PASSWORD_FAILED,
    });
  }
  try {
    return await resetAdminPasswordApi({
      email: challenge.email,
      session: challenge.session,
      newPassword,
    });
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      message:
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.ADMIN_SET_PASSWORD_FAILED,
    });
  }
});

export const refreshSession = createAsyncThunk<
  AuthToken,
  void,
  { rejectValue: AuthErrorResponse }
>("auth/refreshSession", async (_payload, thunkAPI) => {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    return thunkAPI.rejectWithValue({
      message: ERROR_MESSAGES.USER_TOKEN_MISSING,
    });
  }
  try {
    return await refreshTokenApi(refreshToken);
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      message:
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.USER_TOKEN_MISSING,
    });
  }
});

export const ssoLogin = createAsyncThunk<
  AuthToken,
  SSOLoginCredentials,
  { rejectValue: AuthErrorResponse }
>("auth/ssoLogin", async ({ code, redirectUri }, thunkAPI) => {
  try {
    const data = await ssoLoginApi(code, redirectUri);
    const userId = getUserIdFromAccessToken(data.access_token);
    return {
      id_token: data.id_token || "",
      access_token: data.access_token || "",
      refresh_token: data.refresh_token || "",
      expires_in: data.expires_in || null,
      userId,
    };
  } catch (error: unknown) {
    const err = error as {
      response?: { data?: Record<string, unknown> };
      message?: string;
    };
    const data = err.response?.data;
    if (isBlockedByAdminLoginResponse(data)) {
      return thunkAPI.rejectWithValue({
        message:
          (typeof data?.message === "string" && data.message) ||
          ERROR_MESSAGES.ACCOUNT_SUSPENDED,
        statusCode: 403,
        reason: AUTH_ERROR_REASON.BLOCKED_BY_ADMIN,
        isBlockedByAdmin: true,
      });
    }
    return thunkAPI.rejectWithValue({
      message:
        (typeof data?.message === "string" && data.message) ||
        err.message ||
        ERROR_MESSAGES.SSO_LOGIN_FAILED,
      statusCode:
        typeof data?.statusCode === "number" ? data.statusCode : undefined,
    });
  }
});

export const resetForgotPassword = createAsyncThunk<
  ConfirmForgotPasswordResponse,
  ResetPasswordCredentials,
  { rejectValue: AuthErrorResponse }
>(
  "auth/resetForgotPassword",
  async ({ email, otp, password }, { rejectWithValue }) => {
    try {
      const response = await confirmForgotPasswordApi(email, otp, password);
      return response;
    } catch (error: any) {
      return rejectWithValue({
        message:
          error.response?.data?.message ||
          error.message ||
          ERROR_MESSAGES.ERROR_RESET_PASSWORD,
      });
    }
  }
);

export const verifyEmail = createAsyncThunk<
  { message: string },
  { email: string; verificationCode: string },
  { rejectValue: AuthErrorResponse }
>(
  "auth/verifyEmail",
  async ({ email, verificationCode }, { rejectWithValue }) => {
    try {
      const response = await verifyEmailApi(email, verificationCode);
      return response;
    } catch (error: any) {
      return rejectWithValue({
        message:
          error.response?.data?.message ||
          error.message ||
          ERROR_MESSAGES.ERROR_VERIFY_EMAIL,
      });
    }
  }
);

export const signup = createAsyncThunk<
  { message: string },
  SignupCredentials,
  { rejectValue: AuthErrorResponse }
>("auth/signup", async (data, thunkAPI) => {
  try {
    const result = await signupApi(data);
    return { message: result.message };
  } catch (error: any) {
    const responseData = error.response?.data as Record<string, unknown> | undefined;
    if (hasVerifyEmailAction(responseData)) {
      return thunkAPI.rejectWithValue({
        message:
          (typeof responseData?.message === "string" && responseData.message) ||
          ERROR_MESSAGES.SIGNUP_FAILED,
        statusCode:
          typeof responseData?.statusCode === "number"
            ? responseData.statusCode
            : undefined,
        reason: AUTH_ERROR_REASON.VERIFY_EMAIL_REQUIRED,
        email: data.email,
        action:
          typeof responseData?.action === "string" ? responseData.action : undefined,
      });
    }
    if (isEmailRegisteredViaSsoSignupResponse(responseData)) {
      return thunkAPI.rejectWithValue({
        message:
          (typeof responseData?.message === "string" && responseData.message) ||
          ERROR_MESSAGES.EMAIL_ALREADY_REGISTERED_VIA_SSO,
        statusCode: 409,
        reason: AUTH_ERROR_REASON.EMAIL_REGISTERED_VIA_SSO,
      });
    }
    return thunkAPI.rejectWithValue({
      message:
        (typeof responseData?.message === "string" && responseData.message) ||
        error.message ||
        ERROR_MESSAGES.SIGNUP_FAILED,
      statusCode:
        typeof responseData?.statusCode === "number"
          ? responseData.statusCode
          : undefined,
    });
  }
});

export const forgotPassword = createAsyncThunk<
  ForgotPasswordResponse,
  string,
  { rejectValue: AuthErrorResponse }
>("auth/forgotPassword", async (email, thunkAPI) => {
  try {
    const result = await forgotPasswordApi(email);
    return result;
  } catch (error: any) {
    const responseData = error.response?.data as Record<string, unknown> | undefined;
    if (hasVerifyEmailAction(responseData)) {
      return thunkAPI.rejectWithValue({
        message:
          (typeof responseData?.message === "string" && responseData.message) ||
          ERROR_MESSAGES.ERROR_RESET_PASSWORD,
        statusCode:
          typeof responseData?.statusCode === "number"
            ? responseData.statusCode
            : undefined,
        reason: AUTH_ERROR_REASON.VERIFY_EMAIL_REQUIRED,
        email,
        action:
          typeof responseData?.action === "string" ? responseData.action : undefined,
      });
    }
    return thunkAPI.rejectWithValue({
      message: getForgotPasswordRejectedMessage(error),
    });
  }
});

export const confirmForgotPassword = createAsyncThunk<
  ConfirmForgotPasswordResponse,
  { email: string; code: string; newPassword: string },
  { rejectValue: { message: string } }
>("auth/confirmForgotPassword", async (payload, { rejectWithValue }) => {
  try {
    const response = await confirmForgotPasswordApi(
      payload.email,
      payload.code,
      payload.newPassword
    );
    return response as ConfirmForgotPasswordResponse;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || error.message,
    });
  }
});

export const resendConfirmationCode = createAsyncThunk<
  { message: string },
  string,
  { rejectValue: AuthErrorResponse }
>("auth/resendConfirmationCode", async (email, { rejectWithValue }) => {
  try {
    const response = await resendConfirmationCodeApi(email);
    return response;
  } catch (error: any) {
    return rejectWithValue({
      message:
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.ERROR_RESEND_CONFIRMATION_CODE,
    });
  }
});

export const changePassword = createAsyncThunk<
  { message: string },
  ChangePasswordCredentials,
  { rejectValue: AuthErrorResponse }
>(
  "auth/changePassword",
  async ({ oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await changePasswordApi(oldPassword, newPassword);
      return response;
    } catch (error: any) {
      return rejectWithValue({
        message: getApiErrorMessage(error, ERROR_MESSAGES.ERROR_CHANGE_PASSWORD),
      });
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setToken(state, action: PayloadAction<AuthToken>) {
      updateTokens(state, action.payload);
    },
    setUserId(state, action: PayloadAction<string>) {
      state.userId = action.payload;
      tokenStorage.getTokens(); // Update localStorage
      localStorage.setItem(TOKEN_KEYS.USER_ID, action.payload);
    },
    clearToken(state) {
      state.token = null;
      state.accessToken = null;
      state.userAccessToken = null;
      state.refreshToken = null;
      state.userId = null;
      state.isAdmin = false;
      state.isSuperAdmin = false;
      state.error = null;
      state.expiresIn = null;
      state.adminPasswordChallenge = null;
      tokenStorage.clearTokens();
    },
    clearAdminPasswordChallenge(state) {
      state.adminPasswordChallenge = null;
    },
    clearAdminInviteModalFeedback(state) {
      state.error = null;
      state.success = false;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<LoginFulfilledPayload>) => {
        const payload = action.payload;
        if (payload.outcome === LOGIN_API_RESULT_KIND.NEW_PASSWORD_REQUIRED) {
          state.loading = false;
          state.error = null;
          state.adminPasswordChallenge = {
            email: payload.email,
            session: payload.session,
            challenge: payload.challenge,
          };
          return;
        }
        updateTokens(state, payload.tokens);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.token = null;
        state.error = action.payload?.message || ERROR_MESSAGES.LOGIN_FAILED;
        tokenStorage.clearTokens();
      })
      // Signup
      .addCase(signup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signup.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(signup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || ERROR_MESSAGES.SIGNUP_FAILED;
      })
      // SSO Login
      .addCase(ssoLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(ssoLogin.fulfilled, (state, action) => {
        updateTokens(state, action.payload);
        state.success = true;
        state.message = SUCCESS_MESSAGES.SSO_LOGIN_SUCCESS;
      })
      .addCase(ssoLogin.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || ERROR_MESSAGES.SSO_LOGIN_FAILED;
        state.success = false;
      })
      .addCase(refreshSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshSession.fulfilled, (state, action) => {
        updateTokens(state, action.payload);
        state.loading = false;
        state.success = true;
      })
      .addCase(refreshSession.rejected, (state, action) => {
        state.loading = false;
        state.token = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.userId = null;
        state.isAdmin = false;
        state.isSuperAdmin = false;
        state.error =
          action.payload?.message || ERROR_MESSAGES.USER_TOKEN_MISSING;
        tokenStorage.clearTokens();
      })
      // Verify Email
      .addCase(verifyEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message =
          action.payload?.message || SUCCESS_MESSAGES.EMAIL_VERIFIED_SUCCESS;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to verify email";
      })
      .addCase(confirmForgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmForgotPassword.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(confirmForgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Password reset failed";
      })
      // Resend Confirmation Code
      .addCase(resendConfirmationCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendConfirmationCode.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message =
          action.payload?.message || SUCCESS_MESSAGES.OTP_RESENT_SUCCESS;
      })
      .addCase(resendConfirmationCode.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message ||
          ERROR_MESSAGES.ERROR_RESEND_CONFIRMATION_CODE;
      })
      // Reset Forgot Password
      .addCase(resetForgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetForgotPassword.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(resetForgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || ERROR_MESSAGES.ERROR_RESET_PASSWORD;
      })
      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.success = true;
        state.message =
          action.payload?.message || SUCCESS_MESSAGES.PASSWORD_RESET_SUCCESSFUL;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || ERROR_MESSAGES.ERROR_CHANGE_PASSWORD;
        state.success = false;
      })
      .addCase(inviteAdmin.pending, (state) => {
        state.adminInviteLoading = true;
        state.error = null;
      })
      .addCase(inviteAdmin.fulfilled, (state, action) => {
        state.adminInviteLoading = false;
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(inviteAdmin.rejected, (state, action) => {
        state.adminInviteLoading = false;
        state.error =
          action.payload?.message || ERROR_MESSAGES.ADMIN_INVITE_FAILED;
        state.success = false;
      })
      .addCase(resetAdminPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetAdminPassword.fulfilled, (state, action) => {
        updateTokens(state, action.payload);
        state.success = true;
        state.message = SUCCESS_MESSAGES.ADMIN_PASSWORD_SET_SUCCESS;
      })
      .addCase(resetAdminPassword.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || ERROR_MESSAGES.ADMIN_SET_PASSWORD_FAILED;
        state.success = false;
        state.adminPasswordChallenge = null;
      });
  },
});

export const {
  setToken,
  setUserId,
  clearToken,
  clearAdminPasswordChallenge,
  clearAdminInviteModalFeedback,
} = authSlice.actions;
export const logout = clearToken;
export default authSlice.reducer;
export const userIdSelector = (state: RootState) => state.auth.userId;
export const isAdminSelector = (state: RootState) => state.auth.isAdmin;
export const authSelector = (state: RootState) => state.auth;
export const adminPasswordChallengeSelector = (state: RootState) =>
  state.auth.adminPasswordChallenge;
